import { useApolloClient, useMutation } from "@apollo/client";
import { Drawer, Empty, message, Modal } from "antd";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import React, { useContext, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LuPencil, LuTrash2 } from "react-icons/lu";
import { UserContext } from "../../../amplify/authenticator-provider";
import { DELETE_SCHEDULED_MESSAGE } from "../../../graphql/mutation";
import useLang from "../../../hooks/useLang";
import { openDialog } from "../../../library/helpers";
import { DIALOG_TYPES } from "../../hoc/Dialog/dialog";
import { removeScheduledMessage } from "../../../redux/slices/leadSlice";
import ScheduleMessagesPopup from "./scheduleMessagesPopup";
import ScheduledItemCard from "../../commonComponents/ScheduledItemCard";
import CustomEmpty from "../../commonComponents/CustomEmpty";

const localizedFormat = require("dayjs/plugin/localizedFormat");

dayjs.extend(localizedFormat);

const ViewScheduledMessagesPopup = props => {
  const {
    selectedLead,
    getMessages,
    messageText,
    scheduledMessageDrawer,
    setscheduledMessageDrawer,
  } = props;
  const [lang] = useLang();
  const dispatch = useDispatch();
  const { user } = useContext(UserContext);
  const { leadScheduledMessages: scheduledMessages } = useSelector(state => state?.lead);
  // Determine the correct timezone based on context
  const timeZone = useMemo(() => {
    const currentLocationId = sessionStorage.getItem("currentLocationId");

    // Multi-location context: use user's location timezone
    if (currentLocationId?.includes(",")) {
      return user?.locations?.find(item => item.location.id === user?.locationId)?.location
        ?.timezone;
    }

    // Single location context: use session location timezone
    const sessionLocation = user?.locations?.find(item => item.location.id === currentLocationId);
    return sessionLocation?.location?.timezone;
  }, [user?.locations, user?.locationId]);
  const timeZoneObj = timeZone;

  return (
    <Drawer
      open={scheduledMessageDrawer.open}
      width="50%"
      className="normal-popup"
      title="Scheduled Messages"
      onClose={() => setscheduledMessageDrawer({ open: false, messages: null })}
    >
      <div>
        {isEmpty(scheduledMessages) ? (
          <CustomEmpty
            image={Empty.PRESENTED_IMAGE_DEFAULT}
            className="py-10"
            description={
              <span className="text-grey-500">
                Nothing scheduled — messages you schedule will wait here until they send.
              </span>
            }
          />
        ) : (
          <div className="flex-col-centered gap-1">
            {scheduledMessages.map((item, index) => {
              return (
                <ScheduledMessage
                  key={index}
                  index={index}
                  item={item}
                  timeZone={timeZoneObj}
                  scheduledMessages={scheduledMessages}
                  getMessages={getMessages}
                  selectedLead={selectedLead}
                  lang={lang}
                  setscheduledMessageDrawer={setscheduledMessageDrawer}
                />
              );
            })}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default ViewScheduledMessagesPopup;
function ScheduledMessage({
  index,
  item,
  timeZone,
  scheduledMessages,
  getMessages,
  selectedLead,
  lang,
  setscheduledMessageDrawer,
}) {
  const dispatch = useDispatch();
  const client = useApolloClient();
  const [deleteScheduledMessage, { loading: deleteLoading }] =
    useMutation(DELETE_SCHEDULED_MESSAGE);
  const [scheduleMessageForm, setscheduleMessageForm] = useState({
    open: false,
  });
  const [stopCampaignConfirmation, setstopCampaignConfirmation] = useState({
    visible: false,
    item: {},
  });
  const [expanded, setexpanded] = useState(false);
  const handleDeleteMessage = async (index, record) => {
    try {
      const variables = {
        id: record?.id,
        timezone: dayjs.tz.guess(),
      };
      const resp = await deleteScheduledMessage({
        variables,
      });
      if (resp?.data?.deleteScheduledMessage?.statusCode === 200) {
        message.success("Scheduled message deleted successfully");
        if (index > -1) {
          dispatch(removeScheduledMessage(index));
        }
        // Refresh the list from the server as well as the optimistic splice
        // above — same as the scheduled-EMAIL cancel. The query lives in
        // engagementActivity, so it is refreshed by operation name.
        client
          .refetchQueries({ include: ["getScheduledMessagesForLead"] })
          .catch(err => console.error("scheduled messages refetch failed", err));
        getMessages({
          variables: {
            leadId: selectedLead?.id,
            page: 1,
          },
        });
        setscheduledMessageDrawer({ open: false });
      } else {
        console.error(resp?.data?.deleteScheduledMessage?.message);
        message.error("Failed to delete scheduled message. Please try again or contact support.");
      }
    } catch (err) {
      console.error(
        "Failed to delete scheduled message. Please try again or contact support. err =",
        err
      );
      message.error("Failed to delete scheduled message. Please try again or contact support.");
    }
  };
  const maxCount = 270;
  // Same confirmation dialog the Business Hours screens use — it owns its own
  // loading state and closes itself once `onOk` resolves.
  const handleDeletePopup = () => {
    openDialog(DIALOG_TYPES.NEW_CONFIRM_DIALOG, {
      dialogHeader: lang.deleteSchMsgConfPopupTitle,
      headerLine1: lang.deleteSchMsgConfPopupBody,
      onOk: () => handleDeleteMessage(index, item),
      centered: true,
      // Opened from inside the Scheduled Messages drawer, so it has to clear it.
      zIndex: 1100,
      okBtnText: lang.ok,
      cancelBtnText: lang.cancel,
      isDanger: true,
    });
  };

  return (
    <div key={index}>
      <ScheduledItemCard
        sendAt={item?.taskSendDatetime}
        avatarText={item?.user?.fullName}
        meta={`By ${item?.user?.fullName}`}
        clampPreview={false}
        preview={
          <>
            {item.message.length > maxCount && !expanded
              ? `${item.message.slice(0, maxCount)}...`
              : item.message}
            {item.message.length > maxCount && (
              <span
                className="text-brand-500 cursor-pointer ml-1"
                onClick={() => setexpanded(!expanded)}
              >
                {expanded ? "less" : "more"}
              </span>
            )}
          </>
        }
        actions={
          <div className="flex items-center gap-3">
            <LuPencil
              size={16}
              strokeWidth={1.75}
              className="cursor-pointer text-grey-500"
              onClick={() =>
                item?.isTaskExecuted
                  ? () => message.error("This message is already sent, cannot edit it now")
                  : setscheduleMessageForm({ open: true })
              }
            />
            <LuTrash2
              size={16}
              strokeWidth={1.75}
              className="cursor-pointer text-error-500"
              onClick={() =>
                item?.isTaskExecuted
                  ? () => message.error("This message is already sent, cannot delete it now")
                  : handleDeletePopup()
              }
            />
          </div>
        }
      />
      <Modal
        footer={null}
        className="normal-popup"
        onCancel={() => setscheduleMessageForm({ open: false })}
        title="Edit scheduled message"
        open={scheduleMessageForm.open}
      >
        <ScheduleMessagesPopup
          messageData={item}
          close={() => setscheduleMessageForm({ open: false })}
          mode="EDIT"
          scheduledMessages={scheduledMessages}
          selectedLead={selectedLead}
          getMessages={getMessages}
        />
      </Modal>
    </div>
  );
}
